import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import {
  SHAPEFILE_LIST_QUERY_KEYS,
  Shapefile
} from "../../queries/useShapefilesListQuery";

export interface IUseUpdateProjectLinkedShapefileMutationParams {
  projectId: string;
  onMutate?: () => void;
  onSuccess?: (data) => void;
  onError?: (error) => void;
  onSettled?: () => void;
}

export type PropertyT = {
  key: string;
  value: string;
};

export type UpdateProjectLinkedShapefileParamsT = {
  body: {
    key: string;
    value: string;
  };
  projectId: string;
  projectShapefileId: string;
  shapefileId: string;
};

async function updateProjectLinkedShapefile(params: UpdateProjectLinkedShapefileParamsT) {
  const url = `${process.env.REACT_APP_PROJECT_SERVICE}/projectShapefile/update/${params.shapefileId}/${params.projectShapefileId}`;
  const body = params.body;
  return await axios.put(url, body);
}

/**
 * Linked refers to System or organization shapefiles.
 * @param param0
 * @returns
 */
export function useUpdateProjectLinkedShapefileMutation({
  onMutate,
  onSuccess,
  onError,
  onSettled
}: IUseUpdateProjectLinkedShapefileMutationParams) {
  const queryClient = useQueryClient();

  return useMutation(
    (params: UpdateProjectLinkedShapefileParamsT) =>
      updateProjectLinkedShapefile({ ...params }),
    {
      onMutate: async (params: UpdateProjectLinkedShapefileParamsT) => {
        await queryClient.cancelQueries({
          queryKey: [SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList]
        });

        const previousData = queryClient.getQueriesData<{ data: Shapefile[] }>({
          queryKey: [SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList]
        });

        queryClient.setQueriesData<{ data: Shapefile[] }>(
          {
            predicate: (query) =>
              query.queryKey[0] === SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList
          },
          (oldData) => {
            if (!oldData?.data) return oldData;

            return {
              ...oldData,
              data: oldData.data.map((shapefile) => {
                if (
                  shapefile.shapefileId === params.shapefileId ||
                  shapefile.projectShapefileId === params.projectShapefileId
                ) {
                  return {
                    ...shapefile,
                    [params.body.key]: params.body.value,
                    features: shapefile.features.map((feature) => ({
                      ...feature,
                      [params.body.key]: params.body.value
                    }))
                  };
                }
                return shapefile;
              })
            };
          }
        );

        onMutate && onMutate();

        return { previousData };
      },
      onSuccess: (data) => {
        onSuccess && onSuccess(data);
      },
      onError: (error, variables, context) => {
        if (context?.previousData) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          });
        }

        queryClient.invalidateQueries({
          queryKey: [SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList]
        });

        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
